import { useState, useRef } from "react";
import { api, useAuth } from "@/App";
import { Camera, ImagePlus, X, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

// Photo Uploader Component
function PhotoUploader({ vehicleId, onUpload }) {
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const maxSize = 800;
            let width = img.width;
            let height = img.height;
            
            if (width > maxSize || height > maxSize) {
              if (width > height) {
                height = (height / width) * maxSize;
                width = maxSize;
              } else {
                width = (width / height) * maxSize;
                height = maxSize;
              }
            }
            
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', 0.7));
          } catch (err) {
            reject(err);
          }
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };
  
  const handlePhotoCapture = async (e) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    
    // Convertir FileList en Array immédiatement
    const files = Array.from(fileList);
    
    setUploading(true);
    
    try {
      // Compresser toutes les images
      const compressedPhotos = [];
      for (const file of files) {
        const compressed = await compressImage(file);
        compressedPhotos.push(compressed);
      }
      
      // Récupérer les photos existantes
      const vehicleRes = await api.get(`/vehicles/${vehicleId}`);
      const currentPhotos = vehicleRes.data.photos || [];
      
      // Mettre à jour avec les nouvelles photos
      await api.put(`/vehicles/${vehicleId}`, {
        photos: [...currentPhotos, ...compressedPhotos]
      });
      
      toast.success(`${files.length} photo(s) ajoutée(s)`);
      onUpload();
    } catch (error) {
      console.error("Erreur upload photo:", error);
      toast.error("Erreur lors de l'upload");
    }
    
    setUploading(false);
    // Reset input
    if (e.target) e.target.value = '';
  };
  
  return (
    <div className="flex gap-2">
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handlePhotoCapture}
        className="hidden"
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handlePhotoCapture}
        className="hidden"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => cameraInputRef.current?.click()}
        disabled={uploading}
        className="text-red-600 border-red-300 hover:bg-red-50"
      >
        {uploading ? <div className="spinner w-4 h-4" /> : <Camera className="h-4 w-4 mr-1" />}
        Photo
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => galleryInputRef.current?.click()}
        disabled={uploading}
      >
        <ImagePlus className="h-4 w-4 mr-1" />
        Galerie
      </Button>
    </div>
  );
}

// Photo Thumbnail
function PhotoThumbnail({ photo, index, vehicleId, canDelete, allPhotos, onDelete }) {
  const [deleting, setDeleting] = useState(false);
  
  const handleDelete = async () => {
    setDeleting(true);
    const newPhotos = allPhotos.filter((_, i) => i !== index);
    try {
      await api.put(`/vehicles/${vehicleId}`, { photos: newPhotos });
      toast.success("Photo supprimée");
      onDelete();
    } catch (e) {
      toast.error("Erreur");
    }
    setDeleting(false);
  };
  
  const handleDownload = () => {
    try {
      const link = document.createElement('a');
      link.href = photo;
      link.download = `photo_${index + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Téléchargement démarré");
    } catch (e) {
      // Fallback: ouvrir dans un nouvel onglet
      window.open(photo, '_blank');
    }
  };
  
  return (
    <div className="relative group">
      <img 
        src={photo} 
        alt={`Photo ${index + 1}`} 
        className="w-full h-24 object-cover rounded-lg border border-slate-200 cursor-pointer hover:opacity-80"
        onClick={() => window.open(photo, '_blank')}
      />
      <div className="absolute bottom-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={handleDownload}
          className="bg-blue-500 text-white rounded-full p-1.5 hover:bg-blue-600"
          title="Télécharger"
        >
          <Download className="h-3 w-3" />
        </button>
      </div>
      {canDelete && (
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Supprimer"
        >
          {deleting ? <div className="spinner w-3 h-3" /> : <X className="h-3 w-3" />}
        </button>
      )}
    </div>
  );
}

// Main Photos Section Component
export default function VehiclePhotos({ vehicleId, photos = [], canEdit = false, onUpdate }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  
  return (
    <Card className="border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Camera className="h-5 w-5" />
          Photos ({photos.length})
        </CardTitle>
        {canEdit && (
          <PhotoUploader vehicleId={vehicleId} onUpload={onUpdate} />
        )}
      </CardHeader>
      <CardContent>
        {photos.length > 0 ? (
          <div className="grid grid-cols-3 gap-3">
            {photos.map((photo, index) => (
              <PhotoThumbnail
                key={`${vehicleId}-${index}`}
                photo={photo}
                index={index}
                vehicleId={vehicleId}
                canDelete={isAdmin}
                allPhotos={photos}
                onDelete={onUpdate}
              />
            ))}
          </div>
        ) : (
          <p className="text-center text-slate-500 py-4">Aucune photo</p>
        )}
      </CardContent>
    </Card>
  );
}
